<script setup lang="ts">
import Cookies from 'js-cookie'
import {ChevronDown} from 'lucide-vue-next'
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import Button from '@/components/www/primitives/Button.vue'
import Container from '@/components/www/primitives/Container.vue'
import Logo from '@/components/www/layout/Logo.vue'
import FeaturesMegaMenu from '@/components/www/layout/header/FeaturesMegaMenu.vue'
import ResourceMenuIcon from '@/components/www/layout/header/ResourceMenuIcon.vue'
import ResourcesMegaMenu from '@/components/www/layout/header/ResourcesMegaMenu.vue'
import {featureMenuItems} from '@/components/www/layout/header/featureMenu'
import {resourceMenuMobileItems} from '@/components/www/layout/header/resourceMenu'
import {DEMO_REQUEST_PATH} from '@/components/www/demo-request/demoPage'
import {nordstromDemoPath} from '@/components/www/sell-on-nordstrom/nordstromLp'
import {appUrlTo} from '@/lib/urlTo'

/**
 * Site header (ported from osello.com) — reusable chrome mounted in Www.vue.
 *
 * - Smart sticky: transparent at the top; on scroll-DOWN past ~80px it slides
 *   up out of view, on scroll-UP it slides back in with theme-aware frosted
 *   glass (ivory on light pages, deep green on the homepage). Anti-jitter
 *   (<24px deltas ignored), rAF-batched, passive listener.
 * - `theme="dark"` = light-on-dark content over a dark hero (homepage).
 * - `static` = non-fixed solid header for pages with their own sticky layout
 *   (api-docs / Scalar).
 * - Mobile (<768px): hamburger opens a full-screen overlay with focus trap,
 *   Esc-to-close, soft fade-rise (instant under reduced motion).
 */
const props = withDefaults(defineProps<{
    theme?: 'light' | 'dark'
    static?: boolean
    compact?: boolean
    currentPath: string
}>(), {
    theme: 'light',
    static: false,
    compact: false,
})

const navLinks = [
    {label: 'Case Studies', to: '/case-studies'},
    {label: 'Pricing', to: '/pricing'},
]

const mobileNavLinks = [
    {label: 'Case Studies', to: '/case-studies'},
    {label: 'Company', to: '/about'},
    {label: 'Pricing', to: '/pricing'},
]

const loginUrl = appUrlTo('login')
const dashboardUrl = appUrlTo('stores/last')
// Same tokens as useAuthStore (frontend/lib/util/auth.ts). That store cannot be
// imported here: it pulls the app router and reads `window` at init, which breaks
// www SSR. Read cookies/sessionStorage after mount so prerender stays logged-out.
const isLoggedIn = ref(false)

function hasAppSession(): boolean {
    try {
        if (sessionStorage.getItem('osello_jwt')) return true
    } catch {
        // Private mode / restricted storage.
    }
    try {
        return !!Cookies.get('ojwt')
    } catch {
        return false
    }
}

const headerEl = ref<HTMLElement | null>(null)
const mobileNavEl = ref<HTMLElement | null>(null)
const headerState = ref<'top' | 'hidden' | 'revealed'>('top')
const menuOpen = ref(false)
const menuVisible = ref(false)
type MegaId = 'features' | 'resources'
const megaId = ref<MegaId | null>(null)
const megaOpen = computed(() => megaId.value !== null)
const megaHidden = ref(true)
const mobileFeaturesOpen = ref(false)
const mobileResourcesOpen = ref(false)

let reduced: MediaQueryList | null = null
let desktop: MediaQueryList | null = null

// --- Smart sticky ---------------------------------------------------------
const THRESHOLD = 80 // px — natural transparent state above this
const DELTA = 24 // px — ignore smaller scroll jitters
let lastY = 0
let ticking = false

function updateScrollState(): void {
    ticking = false
    const y = window.scrollY
    if (reduced?.matches) {
        headerState.value = y < THRESHOLD ? 'top' : 'revealed'
        lastY = y
        return
    }
    if (y < THRESHOLD) {
        headerState.value = 'top'
        lastY = y
        return
    }
    const delta = y - lastY
    if (Math.abs(delta) < DELTA) return // accumulate until it clears the jitter floor

    // Never hide while a menu is open or focus is inside the header.
    if (menuOpen.value || megaOpen.value || headerEl.value?.contains(document.activeElement)) {
        headerState.value = 'revealed'
        lastY = y
        return
    }
    headerState.value = delta > 0 ? 'hidden' : 'revealed'
    lastY = y
}

function onScroll(): void {
    if (!ticking) {
        requestAnimationFrame(updateScrollState)
        ticking = true
    }
}

// Keyboard: if focus enters while hidden, slide the header back in.
function onFocusIn(): void {
    if (window.scrollY >= THRESHOLD && headerState.value === 'hidden') {
        headerState.value = 'revealed'
    }
}

// --- Desktop mega menus (Features + Resources; only one open) --------------
let megaCloseTimer = 0
let megaOpenedByClick = false

function openMega(id: MegaId, viaClick = false): void {
    if (!desktop?.matches) return
    window.clearTimeout(megaCloseTimer)
    megaOpenedByClick = viaClick
    megaHidden.value = false
    if (megaId.value) {
        megaId.value = id
        return
    }
    requestAnimationFrame(() => {
        megaId.value = id
    })
}

function closeMega(): void {
    window.clearTimeout(megaCloseTimer)
    megaOpenedByClick = false
    megaId.value = null
    if (reduced?.matches) {
        megaHidden.value = true
    } else {
        megaCloseTimer = window.setTimeout(() => {
            if (!megaId.value) megaHidden.value = true
        }, 280)
    }
}

function scheduleMegaClose(): void {
    window.clearTimeout(megaCloseTimer)
    megaCloseTimer = window.setTimeout(() => {
        if (!megaOpenedByClick) closeMega()
    }, 120)
}

function cancelMegaClose(): void {
    window.clearTimeout(megaCloseTimer)
}

function onMegaTriggerClick(id: MegaId): void {
    if (megaId.value === id) closeMega()
    else openMega(id, true)
}

function onMegaTriggerFocus(id: MegaId): void {
    if (desktop?.matches) openMega(id, false)
}

function onNavItemFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null
    const navItem = (event.currentTarget as HTMLElement)
    if (!next || !navItem.contains(next)) scheduleMegaClose()
}

function megaTriggerEl(id: MegaId): HTMLButtonElement | null {
    return headerEl.value?.querySelector(`[aria-controls="${id}-mega-menu"]`) ?? null
}

function onMegaKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
        event.preventDefault()
        const openId = megaId.value
        closeMega()
        if (openId) megaTriggerEl(openId)?.focus()
        return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const panelId = megaId.value ? `${megaId.value}-mega-menu` : ''
    const items = Array.from(
        headerEl.value?.querySelectorAll<HTMLAnchorElement>(`#${panelId} a[href]`) ?? [],
    )
    if (!items.length) return
    event.preventDefault()
    const idx = items.indexOf(document.activeElement as HTMLAnchorElement)
    const delta = event.key === 'ArrowDown' ? 1 : -1
    items[(idx + delta + items.length) % items.length].focus()
}

function onDocumentClick(event: MouseEvent): void {
    if (!megaId.value) return
    const target = event.target as Node
    const navItem = headerEl.value?.querySelector(`.nav-item--${megaId.value}`)
    const panel = headerEl.value?.querySelector(`#${megaId.value}-mega-menu`)
    if (navItem && panel && !navItem.contains(target) && !panel.contains(target)) closeMega()
}

function panelHidden(id: MegaId): boolean {
    return megaHidden.value && megaId.value !== id
}

function toggleMobileAccordion(id: MegaId): void {
    if (id === 'features') {
        mobileFeaturesOpen.value = !mobileFeaturesOpen.value
        if (mobileFeaturesOpen.value) mobileResourcesOpen.value = false
        return
    }
    mobileResourcesOpen.value = !mobileResourcesOpen.value
    if (mobileResourcesOpen.value) mobileFeaturesOpen.value = false
}

function onDesktopChange(event: MediaQueryListEvent): void {
    if (!event.matches) closeMega()
    if (event.matches && menuOpen.value) closeMenu()
}

// --- Mobile menu -----------------------------------------------------------
let lastFocused: HTMLElement | null = null
let menuHideTimer = 0

function menuFocusables(): HTMLElement[] {
    return Array.from(
        mobileNavEl.value?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? [],
    )
}

function openMenu(): void {
    window.clearTimeout(menuHideTimer)
    lastFocused = document.activeElement as HTMLElement
    menuVisible.value = true
    // Next frame so the transition runs from the hidden start state.
    requestAnimationFrame(() => {
        menuOpen.value = true
    })
    document.documentElement.style.overflow = 'hidden'
    void nextTick(() => {
        const f = menuFocusables()
        if (f.length) f[0].focus()
    })
}

function closeMenu(): void {
    menuOpen.value = false
    document.documentElement.style.overflow = ''
    if (reduced?.matches) {
        menuVisible.value = false
    } else {
        menuHideTimer = window.setTimeout(() => {
            if (!menuOpen.value) menuVisible.value = false
        }, 320)
    }
    lastFocused?.focus()
}

function toggleMenu(): void {
    if (menuOpen.value) closeMenu()
    else openMenu()
}

// Esc closes; Tab is trapped within the overlay.
function onMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
        event.preventDefault()
        closeMenu()
        return
    }
    if (event.key !== 'Tab') return
    const f = menuFocusables()
    if (!f.length) return
    const first = f[0]
    const last = f[f.length - 1]
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
    }
}

// Navigating away closes menus.
watch(() => props.currentPath, () => {
    if (menuOpen.value) closeMenu()
    if (megaOpen.value) closeMega()
    mobileFeaturesOpen.value = false
    mobileResourcesOpen.value = false
})

onMounted(() => {
    isLoggedIn.value = hasAppSession()
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    desktop = window.matchMedia('(min-width: 768px)')
    lastY = window.scrollY
    if (!props.static) {
        window.addEventListener('scroll', onScroll, {passive: true})
        updateScrollState()
    }
    document.addEventListener('click', onDocumentClick)
    desktop.addEventListener('change', onDesktopChange)
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll)
    document.removeEventListener('click', onDocumentClick)
    desktop?.removeEventListener('change', onDesktopChange)
    document.documentElement.style.overflow = ''
})
</script>

<template>
  <header
      ref="headerEl"
      class="site-header"
      :class="{'site-header--static': props.static}"
      :data-header-theme="theme"
      :data-header-state="props.static ? 'revealed' : headerState"
      :data-menu-open="menuOpen ? '' : undefined"
      :data-mega-open="megaId ?? undefined"
      @focusin="onFocusIn"
  >
    <Container width="lg" class="site-header__inner">
      <Logo class="site-header__logo"/>

      <div class="site-header__actions">
        <nav v-if="!compact" class="site-header__nav" aria-label="Primary">
          <ul class="nav-links">
            <li
                class="nav-item nav-item--features"
                @mouseenter="openMega('features', false)"
                @mouseleave="scheduleMegaClose"
                @focusout="onNavItemFocusOut"
            >
              <button
                  type="button"
                  class="nav-link nav-link--mega-trigger"
                  :aria-expanded="megaId === 'features' ? 'true' : 'false'"
                  aria-controls="features-mega-menu"
                  aria-haspopup="true"
                  @click="onMegaTriggerClick('features')"
                  @focus="onMegaTriggerFocus('features')"
              >
                Features
                <ChevronDown class="nav-link__chev" aria-hidden="true"/>
              </button>
            </li>
            <li v-for="link in navLinks" :key="link.to">
              <RouterLink class="nav-link" :to="link.to">{{ link.label }}</RouterLink>
            </li>
            <li
                class="nav-item nav-item--resources"
                @mouseenter="openMega('resources', false)"
                @mouseleave="scheduleMegaClose"
                @focusout="onNavItemFocusOut"
            >
              <button
                  type="button"
                  class="nav-link nav-link--mega-trigger"
                  :aria-expanded="megaId === 'resources' ? 'true' : 'false'"
                  aria-controls="resources-mega-menu"
                  aria-haspopup="true"
                  @click="onMegaTriggerClick('resources')"
                  @focus="onMegaTriggerFocus('resources')"
              >
                Resources
                <ChevronDown class="nav-link__chev" aria-hidden="true"/>
              </button>
            </li>
            <li v-if="!isLoggedIn">
              <a class="nav-link" :href="loginUrl" data-cta="login" data-cta-location="header">Login</a>
            </li>
          </ul>
        </nav>
        <template v-if="compact">
          <Button
              variant="primary"
              class="site-header__cta site-header__cta--compact"
              :to="nordstromDemoPath('header-demo')"
          >
            Book intro call
          </Button>
        </template>
        <Button
            v-else-if="isLoggedIn"
            variant="primary"
            class="site-header__cta"
            :href="dashboardUrl"
            data-cta="dashboard"
            data-cta-location="header"
        >
          My dashboard
        </Button>
        <Button v-else variant="primary" class="site-header__cta" :to="DEMO_REQUEST_PATH" data-cta="demo_request" data-cta-location="header">
          Get a demo
        </Button>
      </div>

      <button
          class="nav-toggle"
          type="button"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-controls="mobile-nav"
          :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
          @click="toggleMenu"
      >
        <span class="nav-toggle__icon" aria-hidden="true">
          <span class="nav-toggle__bar"/>
          <span class="nav-toggle__bar"/>
          <span class="nav-toggle__bar"/>
        </span>
      </button>
    </Container>

    <div
        v-if="!compact"
        @mouseenter="cancelMegaClose"
        @mouseleave="scheduleMegaClose"
        @keydown="onMegaKeydown"
    >
      <FeaturesMegaMenu :open="megaId === 'features'" :hiddenPanel="panelHidden('features')" @link-click="closeMega"/>
      <ResourcesMegaMenu :open="megaId === 'resources'" :hiddenPanel="panelHidden('resources')" @link-click="closeMega"/>
    </div>

    <!-- Mobile overlay — teleported so position:fixed covers the viewport
         (header transform creates a containing block that clips the overlay). -->
    <Teleport to="body">
      <div
          v-show="menuVisible"
          id="mobile-nav"
          ref="mobileNavEl"
          class="mobile-nav"
          :data-open="menuOpen ? 'true' : 'false'"
          @keydown="onMenuKeydown"
      >
        <nav class="mobile-nav__inner" aria-label="Primary mobile">
          <ul v-if="compact" class="mobile-nav__links">
            <li>
              <RouterLink class="mobile-nav__link" :to="nordstromDemoPath('header-demo-mobile')" @click="closeMenu">
                Book intro call
              </RouterLink>
            </li>
            <li>
              <a class="mobile-nav__link" :href="loginUrl">Login</a>
            </li>
          </ul>
          <template v-else>
            <ul class="mobile-nav__links">
              <li class="mobile-nav__group">
                <button
                    type="button"
                    class="mobile-nav__link mobile-nav__accordion-trigger"
                    :aria-expanded="mobileFeaturesOpen ? 'true' : 'false'"
                    aria-controls="mobile-features-sub"
                    @click="toggleMobileAccordion('features')"
                >
                  Features
                  <ChevronDown class="mobile-nav__chev" aria-hidden="true"/>
                </button>
                <ul v-show="mobileFeaturesOpen" id="mobile-features-sub" class="mobile-nav__sub">
                  <li v-for="item in featureMenuItems" :key="item.id">
                    <RouterLink class="mobile-nav__sub-link" :to="item.to" @click="closeMenu">
                      <span class="mobile-nav__sub-title">{{ item.titleLine1 }} {{ item.titleLine2 }}</span>
                      <span class="mobile-nav__sub-desc">{{ item.description }}</span>
                    </RouterLink>
                  </li>
                  <li>
                    <RouterLink class="mobile-nav__sub-all" to="/features" @click="closeMenu">
                      View all features
                      <span aria-hidden="true">&rarr;</span>
                    </RouterLink>
                  </li>
                </ul>
              </li>
              <li v-for="link in mobileNavLinks" :key="link.to">
                <RouterLink class="mobile-nav__link" :to="link.to" @click="closeMenu">
                  {{ link.label }}
                </RouterLink>
              </li>
              <li class="mobile-nav__group">
                <button
                    type="button"
                    class="mobile-nav__link mobile-nav__accordion-trigger"
                    :aria-expanded="mobileResourcesOpen ? 'true' : 'false'"
                    aria-controls="mobile-resources-sub"
                    @click="toggleMobileAccordion('resources')"
                >
                  Resources
                  <ChevronDown class="mobile-nav__chev" aria-hidden="true"/>
                </button>
                <ul v-show="mobileResourcesOpen" id="mobile-resources-sub" class="mobile-nav__sub">
                  <li v-for="item in resourceMenuMobileItems" :key="item.id">
                    <RouterLink class="mobile-nav__sub-link mobile-nav__sub-link--with-icon" :to="item.to" @click="closeMenu">
                      <ResourceMenuIcon :name="item.id"/>
                      <span class="mobile-nav__sub-copy">
                        <span class="mobile-nav__sub-title">{{ item.label }}</span>
                        <span class="mobile-nav__sub-desc">{{ item.description }}</span>
                      </span>
                    </RouterLink>
                  </li>
                  <li>
                    <RouterLink class="mobile-nav__sub-all" to="/resources" @click="closeMenu">
                      Browse all resources
                      <span aria-hidden="true">&rarr;</span>
                    </RouterLink>
                  </li>
                </ul>
              </li>
              <li v-if="!isLoggedIn">
                <a class="mobile-nav__link" :href="loginUrl" data-cta="login" data-cta-location="mobile_nav">Login</a>
              </li>
            </ul>
            <Button
                v-if="isLoggedIn"
                variant="primary"
                class="mobile-nav__cta"
                :href="dashboardUrl"
                data-cta="dashboard"
                data-cta-location="mobile_nav"
                @click="closeMenu"
            >
              My dashboard
            </Button>
            <Button
                v-else
                variant="primary"
                class="mobile-nav__cta"
                :to="DEMO_REQUEST_PATH"
                data-cta="demo_request"
                data-cta-location="mobile_nav"
                @click="closeMenu"
            >
              Get a demo
            </Button>
          </template>
        </nav>
      </div>
    </Teleport>
  </header>
</template>

<style scoped>
.site-header {
    position: fixed;
    inset-block-start: 0;
    inset-inline: 0;
    z-index: 50;
    --header-fg: var(--text-primary);
    --header-fg-hover: var(--primary-500);
    padding-block: var(--space-7);
    border-bottom: 1px solid transparent;
    transform: translateY(0);
    /* No background/filter on the header itself — a backdrop-filter here would
       rasterize its children (the vector logo) into a 1x compositor layer and
       blur them on retina. The glass lives on ::before instead. */
    transition:
        transform 280ms var(--ease-out-strong),
        border-color 280ms var(--ease-out);
}

/* Static variant (api-docs): in-flow solid header, no scroll choreography. */
.site-header--static {
    position: sticky;
    background: var(--header-bg);
    border-bottom-color: var(--header-stroke);
}

/* Frosted glass lives on ::before (not on the header) so the vector logo isn't
   rasterized. Glass is OFF at top and when hidden — only on scroll-UP reveal. */
.site-header::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    opacity: 0;
    pointer-events: none;
    -webkit-backdrop-filter: blur(16px) saturate(1.05);
    backdrop-filter: blur(16px) saturate(1.05);
    transition: opacity 280ms var(--ease-out);
}

.site-header--static::before {
    display: none;
}

/* Hidden — slid up out of view (scrolling DOWN past the threshold). */
.site-header[data-header-state="hidden"] {
    transform: translateY(-100%);
}

.site-header[data-header-state="hidden"]::before {
    opacity: 0;
}

/* Revealed — scroll-UP only: theme-aware frosted bar. */
.site-header:not(.site-header--static)[data-header-state="revealed"] {
    padding-block-start: var(--space-5);
    /* Extra bottom room — logo ink + nav baseline sit close to the bar edge. */
    padding-block-end: var(--space-8);
}

.site-header:not(.site-header--static)[data-header-state="revealed"][data-header-theme="dark"]::before {
    opacity: 1;
    background: linear-gradient(
        180deg,
        color-mix(in srgb, var(--secondary-700) 88%, transparent) 0%,
        color-mix(in srgb, var(--secondary-500) 92%, transparent) 100%
    );
    -webkit-backdrop-filter: blur(12px) saturate(1.15);
    backdrop-filter: blur(12px) saturate(1.15);
}

.site-header:not(.site-header--static)[data-header-state="revealed"][data-header-theme="dark"] {
    border-bottom-color: rgba(255, 255, 255, 0.1);
    --header-fg: rgba(255, 255, 255, 0.92);
    --header-fg-hover: rgba(255, 255, 255, 1);
}

.site-header:not(.site-header--static)[data-header-state="revealed"][data-header-theme="light"]::before {
    opacity: 1;
    background: color-mix(in srgb, var(--surface) 90%, transparent);
}

.site-header:not(.site-header--static)[data-header-state="revealed"][data-header-theme="light"] {
    border-bottom-color: var(--header-stroke);
    --header-fg: var(--text-primary);
    --header-fg-hover: var(--primary-500);
}

/* Logo: green on light pages; white on the dark homepage hero + its reveal bar. */
.site-header[data-header-theme="light"] :deep(.logo) {
    color: var(--primary-500);
}

.site-header[data-header-theme="dark"] :deep(.logo) {
    color: rgba(255, 255, 255, 0.95);
}

.site-header--static :deep(.logo) {
    color: var(--primary-500);
}

/* Dark theme (header sits over a dark hero): light-on-dark content at the top. */
.site-header[data-header-theme="dark"] {
    --header-fg: rgba(255, 255, 255, 0.92);
    --header-fg-hover: var(--primary-300);
}

.site-header :deep(.site-header__inner) {
    display: flex;
    /* Links + CTA align to the logo's bottom baseline, not its centre. */
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--space-8);
    max-width: var(--container-page-lg);
}

/* The wordmark SVG carries transparent space below the letterforms. Pull the
   logo's box bottom down by that amount so flex-end aligns nav + CTA to the
   wordmark's visual baseline, not the empty edge of the SVG box. */
.site-header :deep(.site-header__logo) {
    margin-bottom: calc(-1 * var(--logo-mark-ink-bottom-inset));
}

.site-header :deep(.site-header__logo .logo__mark) {
    height: var(--logo-mark-height);
}

.site-header__actions {
    display: flex;
    align-items: center;
    gap: var(--space-9);
}

.site-header__nav {
    display: flex;
    align-items: center;
}

.nav-links {
    display: flex;
    align-items: center;
    gap: var(--space-9);
    margin: 0;
    padding: 0;
    list-style: none;
}

.nav-links li {
    display: flex;
    align-items: center;
}

.nav-link {
    display: inline-flex;
    align-items: center;
    font-family: var(--font-body);
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 1.6px;
    padding: 0;
    color: var(--header-fg);
    transition: color 160ms var(--ease-out);
}

@media (hover: hover) and (pointer: fine) {
    .nav-link:hover {
        color: var(--header-fg-hover);
    }
}

.nav-link:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 4px;
    border-radius: 4px;
}

.nav-link--mega-trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 0;
    cursor: pointer;
}

.nav-link__chev {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    opacity: 0.72;
    transition: transform 220ms var(--ease-out-strong);
}

.nav-item--features:hover .nav-link__chev,
.nav-item--features:focus-within .nav-link__chev,
.site-header[data-mega-open="features"] .nav-item--features .nav-link__chev,
.nav-item--resources:hover .nav-link__chev,
.nav-item--resources:focus-within .nav-link__chev,
.site-header[data-mega-open="resources"] .nav-item--resources .nav-link__chev {
    transform: rotate(180deg);
}

.site-header[data-mega-open="features"] .nav-item--features .nav-link--mega-trigger,
.site-header[data-mega-open="resources"] .nav-item--resources .nav-link--mega-trigger {
    color: var(--header-fg-hover);
}

.site-header :deep(.site-header__cta) {
    flex-shrink: 0;
    /* Match pre-footer-cta__btn height. */
    padding: 14px 28px;
    border-radius: var(--radius-button);
    font-weight: var(--fw-semibold);
    line-height: 1;
    transition:
        transform 160ms var(--ease-out-strong),
        background-color 180ms var(--ease-out);
}

@media (hover: hover) and (pointer: fine) {
    .site-header :deep(.site-header__cta:hover) {
        transform: translateY(-1px);
    }
}

.site-header :deep(.site-header__cta:active) {
    transform: scale(0.97);
}

/* Hamburger — hidden on desktop. */
.nav-toggle {
    display: none;
    align-self: center;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: transparent;
    border: 0;
    cursor: pointer;
    color: var(--header-fg);
    border-radius: var(--radius-md);
    transition: transform 140ms var(--ease-out-strong);
}

.nav-toggle__icon {
    position: relative;
    display: block;
    width: 22px;
    height: 18px;
}

.nav-toggle:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
}

.nav-toggle__bar {
    position: absolute;
    left: 0;
    top: 50%;
    width: 22px;
    height: 2px;
    margin-top: -1px;
    border-radius: 2px;
    background: currentColor;
    transform-origin: center;
    transition:
        transform 0.3s var(--ease-out),
        opacity 0.2s var(--ease-out);
}

.nav-toggle__bar:nth-child(1) {
    transform: translateY(-7px);
}

.nav-toggle__bar:nth-child(2) {
    transform: translateY(0);
}

.nav-toggle__bar:nth-child(3) {
    transform: translateY(7px);
}

/* Morph to an X on the same center axis. */
.site-header[data-menu-open] .nav-toggle__bar:nth-child(1) {
    transform: translateY(0) rotate(45deg);
}

.site-header[data-menu-open] .nav-toggle__bar:nth-child(2) {
    opacity: 0;
}

.site-header[data-menu-open] .nav-toggle__bar:nth-child(3) {
    transform: translateY(0) rotate(-45deg);
}

@media (hover: hover) and (pointer: fine) {
    .nav-toggle:active {
        transform: scale(0.94);
    }
}

/* Mobile overlay — full-viewport panel (teleported to body). */
.mobile-nav {
    position: fixed;
    inset: 0;
    z-index: 150;
    background-color: var(--surface);
    /* +30px so the links clear the logo comfortably when the panel opens. */
    padding: calc(var(--www-header-sticky-offset, 92px) + var(--space-7) + 30px) var(--space-8)
        calc(80px + var(--space-8) + env(safe-area-inset-bottom, 0px));
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-6px) scale(0.98);
    transition:
        opacity 280ms var(--ease-out-strong),
        transform 280ms var(--ease-out-strong),
        visibility 0s linear 280ms;
}

.mobile-nav[data-open="true"] {
    opacity: 1;
    visibility: visible;
    transform: none;
    transition:
        opacity 280ms var(--ease-out-strong),
        transform 280ms var(--ease-out-strong),
        visibility 0s linear 0s;
}

.mobile-nav__inner {
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.mobile-nav__links {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-7);
    margin: 0;
    padding: 0;
    list-style: none;
}

.mobile-nav__link {
    font-family: var(--font-heading);
    font-weight: var(--fw-medium);
    font-size: calc(var(--type-display-md-size) - 8px);
    line-height: 1.1;
    letter-spacing: var(--type-display-md-ls);
    color: var(--text-primary);
    transition: color 200ms var(--ease-out);
}

@media (hover: hover) and (pointer: fine) {
    .mobile-nav__link:hover {
        color: var(--primary-500);
    }
}

.mobile-nav__link:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 4px;
    border-radius: 6px;
}

.site-header__actions:has(.site-header__cta--compact) {
    gap: var(--space-4);
}

/* Dark theme: the ghost compact CTA's default dark ink is invisible over a
   dark hero — flip it to light. */
.site-header[data-header-theme="dark"] :deep(.site-header__cta--compact.btn--ghost) {
    color: rgba(255, 255, 255, 0.92);
    border-color: rgba(255, 255, 255, 0.35);
}

/* Green-on-green blends into the dark gradient — the primary compact CTA goes
   white with dark green ink (matches the hero CTA on the Nordstrom LP). */
.site-header[data-header-theme="dark"] :deep(.site-header__cta--compact.btn--primary) {
    background-color: var(--text-white);
    color: var(--secondary-500);
}

@media (hover: hover) and (pointer: fine) {
    .site-header[data-header-theme="dark"] :deep(.site-header__cta--compact.btn--ghost:hover) {
        background-color: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.6);
    }

    .site-header[data-header-theme="dark"] :deep(.site-header__cta--compact.btn--primary:hover) {
        background-color: rgba(247, 245, 242, 0.85);
    }
}

.mobile-nav__accordion-trigger {
    display: inline-flex;
    align-items: center;
    gap: var(--space-5);
    width: 100%;
    text-align: start;
    background: transparent;
    border: 0;
    cursor: pointer;
    padding: 0;
}

.mobile-nav__chev {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: var(--text-secondary);
    transition: transform 0.25s var(--ease-out);
}

.mobile-nav__accordion-trigger[aria-expanded="true"] .mobile-nav__chev {
    transform: rotate(180deg);
}

.mobile-nav__sub {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    margin: var(--space-7) 0 0;
    padding: 0;
    list-style: none;
}

.mobile-nav__sub > li {
    display: flex;
    min-height: 0;
}

.mobile-nav__sub-link {
    display: block;
    width: 100%;
    padding: var(--space-3) 0;
    border-radius: var(--radius-xl);
    color: inherit;
    text-decoration: none;
    transition:
        background-color 0.2s var(--ease-out),
        color 0.2s var(--ease-out);
}

.mobile-nav__sub-link:has(.mobile-nav__sub-copy) {
    display: flex;
    align-items: center;
    gap: 12px;
}

.mobile-nav__sub-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.mobile-nav__sub-link:hover,
.mobile-nav__sub-link:focus-visible {
    background: var(--accent-50);
}

.mobile-nav__sub-link:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 4px;
    border-radius: 6px;
}

/* Green "view all" link closing the features accordion (mobile twin of
   the desktop mega menu's "Explore all features"). */
.mobile-nav__sub-all {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) 0;
    font-family: var(--font-heading);
    font-weight: var(--fw-medium);
    font-size: var(--type-heading-sm-size);
    letter-spacing: -0.01em;
    color: var(--primary-500);
    text-decoration: none;
}

.mobile-nav__sub-all:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 4px;
    border-radius: 6px;
}

.mobile-nav__sub-title {
    display: block;
    margin-block-end: var(--space-3);
    font-family: var(--font-heading);
    font-weight: var(--fw-medium);
    font-size: var(--type-heading-sm-size);
    line-height: 1.2;
    letter-spacing: -0.01em;
    color: var(--secondary-500);
    white-space: nowrap;
    transition: color 0.2s var(--ease-out);
}

.mobile-nav__sub-link:hover .mobile-nav__sub-title,
.mobile-nav__sub-link:focus-visible .mobile-nav__sub-title {
    color: var(--primary-500);
}

.mobile-nav__sub-desc {
    font-family: var(--font-body);
    font-size: var(--type-body-sm-size);
    line-height: 1.55;
    color: var(--text-secondary);
    max-width: 28ch;
}

.mobile-nav :deep(.mobile-nav__cta) {
    position: fixed;
    left: var(--space-8);
    right: var(--space-8);
    bottom: calc(var(--space-8) + env(safe-area-inset-bottom, 0px));
    z-index: 151;
    width: auto;
    margin-top: 0;
    padding: 14px 28px;
    border-radius: var(--radius-button);
    font-weight: var(--fw-semibold);
    line-height: 1;
}

@media (max-width: 767px) {
    .site-header__actions {
        display: none;
    }

    .site-header[data-menu-open] {
        z-index: 160;
        --header-fg: var(--text-primary);
        --header-fg-hover: var(--primary-500);
    }

    .site-header[data-menu-open][data-header-theme="dark"] :deep(.logo) {
        color: var(--primary-500);
    }

    .nav-toggle {
        display: inline-flex;
    }

    .site-header :deep(.site-header__inner) {
        align-items: center;
    }

    .site-header :deep(.site-header__logo) {
        margin-bottom: 0;
    }

    .site-header :deep(.site-header__logo .logo__mark) {
        height: calc(var(--logo-mark-height) * 0.7);
    }
}

@media (prefers-reduced-motion: reduce) {
    .site-header {
        transition: none;
    }

    .site-header::before {
        transition: none;
    }

    .mobile-nav {
        transition: none;
        transform: none;
        visibility: visible;
    }

    .nav-link__chev {
        transition: none;
    }
}
</style>
